
import React, { useState } from "react";
import ImageActionButtons from "./ImageActionButtons";

interface ImageRendererProps {
  images?: string[];
  imageUrls?: string[];
  showActionButtons?: boolean;
  unsplashData?: {
    id: string;
    photographer: string;
    photographerUsername: string;
    photographerUrl: string;
    unsplashUrl: string;
    downloadUrl: string;
    downloadEndpoint: string;
    description: string;
  };
}

interface ImageState {
  [key: string]: 'loading' | 'loaded' | 'error';
}

const ImageRenderer: React.FC<ImageRendererProps> = ({ images = [], imageUrls = [], showActionButtons = false, unsplashData }) => {
  const [imageStates, setImageStates] = useState<ImageState>({});
  
  console.log('🖼️ IMGRENDER: ImageRenderer called with:', {
    imagesCount: images.length,
    imageUrlsCount: imageUrls.length,
    images: images.map((img, i) => ({
      index: i,
      length: img.length,
      isBase64: img.startsWith('data:image/'),
      hasComma: img.includes(','),
      preview: img.substring(0, 50) + '...'
    })),
    imageUrls
  });

  const handleImageLoad = (key: string) => {
    console.log(`✅ IMGRENDER: Image ${key} loaded successfully`);
    setImageStates(prev => ({ ...prev, [key]: 'loaded' }));
  };

  const handleImageError = (key: string) => {
    console.error(`❌ IMGRENDER: Image ${key} failed to load`);
    setImageStates(prev => ({ ...prev, [key]: 'error' }));
  };
  
  return (
    <>
      {images.length > 0 && (
        <div className="mb-3 space-y-2">
          {images.map((imageData, index) => {
            const key = `base64-${index}`;
            const state = imageStates[key] || 'loading';
            
            console.log(`🖼️ IMGRENDER: Rendering base64 image ${index + 1}:`, {
              length: imageData.length,
              startsCorrectly: imageData.startsWith('data:image/'),
              hasBase64: imageData.includes(';base64,'),
              state,
              actualDataPreview: imageData.substring(0, 100)
            });
            
            return (
              <div key={key}>
                <img
                  src={imageData}
                  alt={`Uploaded image ${index + 1}`}
                  className="max-w-full h-auto rounded-lg border border-gray-200"
                  style={{ maxHeight: '300px', objectFit: 'contain' }}
                  onLoad={() => handleImageLoad(key)}
                  onError={() => handleImageError(key)}
                />
                {state === 'error' && (
                  <div className="text-red-500 text-sm mt-1">
                    Failed to load image. Please try again.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {imageUrls.length > 0 && (
        <div className="mb-3 space-y-2">
          {imageUrls.map((imageUrl, index) => {
            const key = `url-${index}`;
            const state = imageStates[key] || 'loading';
            
            console.log(`🖼️ IMGRENDER: Rendering image URL ${index + 1}:`, imageUrl);
            
            return (
              <div key={key}>
                <img
                  src={imageUrl}
                  alt=""
                  className="max-w-full h-auto rounded-lg"
                  style={{ maxHeight: '300px', objectFit: 'contain' }}
                  onLoad={() => handleImageLoad(key)}
                  onError={() => handleImageError(key)}
                />
                {state === 'error' && (
                  <div className="text-amber-600 text-sm mt-1">
                    Image load failed, but you can still use the action buttons below
                  </div>
                )}
                {(showActionButtons || unsplashData) && (
                  <div className="flex justify-between items-center mt-2">
                    {unsplashData && (
                      <div className="text-xs text-muted-foreground">
                        Photo by{" "}
                        <a 
                          href={unsplashData.photographerUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="underline hover:opacity-80"
                        >
                          {unsplashData.photographer}
                        </a>
                        {" "}on{" "}
                        <a 
                          href="https://unsplash.com" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="underline hover:opacity-80"
                        >
                          Unsplash
                        </a>
                      </div>
                    )}
                    {showActionButtons && (
                      <ImageActionButtons 
                        imageUrl={imageUrl} 
                        unsplashData={unsplashData}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default ImageRenderer;
